import { label } from '@/components/ui/embedded-resource-table/descriptor';
import { Badge } from '@/components/ui/shadcn/badge';
import { Checkbox } from '@/components/ui/shadcn/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { TableColumnHeader } from '@/components/ui/table-cells/TableColumnHeader';
import { useTranslations } from '@/hooks/useTranslations';
import { settingLabel } from '@/lib/status-labels';
import { cn } from '@/lib/utils';
import { type Company, type User } from '@/types';
import { Link, router } from '@inertiajs/react';
import { companyTableDescriptor } from './company-table-descriptor';
import { InlineAssignedUserCell } from './inline-cells/InlineAssignedUserCell';
import { InlineDomainCell } from './inline-cells/InlineDomainCell';
import { InlinePriorityCell } from './inline-cells/InlinePriorityCell';
import { InlineTypeCell } from './inline-cells/InlineTypeCell';

interface CompanyTableProps {
    companies: Company[];
    users?: User[];
    onUpdate?: {
        name?: (companyId: string, value: string) => Promise<void>;
        domain?: (companyId: string, value: string | null) => Promise<void>;
        type?: (companyId: string, value: string) => Promise<void>;
        priority?: (companyId: string, value: string) => Promise<void>;
        assignedUsers?: (companyId: string, userIds: string[]) => Promise<void>;
    };
    sortColumn?: string | null;
    sortDirection?: 'asc' | 'desc' | null;
    onSortChange?: (column: string, direction: 'asc' | 'desc') => void;
    onColumnHide?: (column: string) => void;
    onColumnShow?: (column: string) => void;
    visibleColumns?: Set<string>;
    columnFilters?: Record<string, string[]>;
    columnFilterOptions?: Record<string, Array<{ value: string; label: string }>>;
    onFilterChange?: (column: string, values: string[]) => void;
    types?: string[];
    priorities?: Array<{ value: string; label: string; color: string; icon?: string }>;
    readOnly?: boolean;
    /** Swaps row clicks from navigation to selection and reveals the checkbox column. */
    selectionMode?: boolean;
    selectedIds?: Set<string>;
    onToggleSelect?: (id: string) => void;
    onToggleSelectAll?: () => void;
}

/** Categories are multi-value; cap the badges so one row can't blow out the column. */
const MAX_CATEGORY_BADGES = 2;

export function CompanyTable({
    companies,
    users = [],
    onUpdate,
    sortColumn,
    sortDirection,
    onSortChange,
    onColumnHide,
    visibleColumns = new Set(companyTableDescriptor.defaultVisibleColumns),
    columnFilters = {},
    columnFilterOptions = {},
    onFilterChange,
    types = [],
    priorities = [],
    readOnly = false,
    selectionMode = false,
    selectedIds,
    onToggleSelect,
    onToggleSelectAll,
}: CompanyTableProps) {
    const { t } = useTranslations();

    const handleRowClick = (companyId: string) => {
        router.visit(`/company/${companyId}`);
    };

    const handleSort = (column: string, direction: 'asc' | 'desc') => {
        onSortChange?.(column, direction);
    };

    const handleColumnHide = (column: string) => {
        onColumnHide?.(column);
    };

    // `sortable` mirrors the backend's $dbSortColumns whitelist in
    // CompanyController@index (plus total_profit, which it sorts in PHP).
    // Derived from the descriptor rather than restated here: this used to be a
    // third copy of the catalogue, alongside the index page's and the show tabs'.
    // A column with no sort entry offers no sort actions — the controller falls
    // back to ordering by uuid for unrecognised keys, so one would appear to work
    // while quietly reshuffling the table.
    const columns = companyTableDescriptor.columns
        .filter((column) => visibleColumns.has(column.id))
        .map((column) => ({ id: column.id, label: label(t, column), sortable: Boolean(column.sort) }));

    const allSelected = selectionMode && companies.length > 0 && companies.every((company) => selectedIds?.has(company.id));
    const someSelected = selectionMode && !allSelected && companies.some((company) => selectedIds?.has(company.id));

    return (
        <div className="space-y-4">
            <div className="bg-card mb-4 items-center justify-between gap-3 rounded-xl border px-4 py-2.5">
                <Table>
                    <TableHeader>
                        <TableRow>
                            {selectionMode && (
                                <TableHead className="w-10">
                                    <Checkbox
                                        checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                                        onCheckedChange={() => onToggleSelectAll?.()}
                                        aria-label={t('companies.tables.select_all', 'Select all')}
                                    />
                                </TableHead>
                            )}
                            {columns.map((column) => {
                                const isSorted = sortColumn === column.id;
                                const currentSortDirection = isSorted ? sortDirection : null;
                                const selectedFilters = columnFilters[column.id] || [];

                                return (
                                    <TableHead key={column.id}>
                                        <TableColumnHeader
                                            label={column.label}
                                            sortDirection={currentSortDirection}
                                            onSortAscending={column.sortable ? () => handleSort(column.id, 'asc') : undefined}
                                            onSortDescending={column.sortable ? () => handleSort(column.id, 'desc') : undefined}
                                            onHide={() => handleColumnHide(column.id)}
                                            filterOptions={columnFilterOptions[column.id] || []}
                                            selectedFilters={selectedFilters}
                                            onFilterChange={(values) => onFilterChange?.(column.id, values)}
                                            isFiltered={selectedFilters.length > 0}
                                        />
                                    </TableHead>
                                );
                            })}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {companies.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length + (selectionMode ? 1 : 0)} className="text-muted-foreground py-8 text-center">
                                    {t('companies.index.no_companies_found', 'No companies found.')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            companies.map((company) => {
                                const assignedUsers = company.users || [];
                                const isSelected = selectionMode && selectedIds?.has(company.id);

                                return (
                                    <TableRow
                                        key={company.id}
                                        data-state={isSelected ? 'selected' : undefined}
                                        className={cn(
                                            'hover:bg-muted/50 transition-all',
                                            selectionMode ? 'cursor-default' : 'cursor-pointer',
                                            isSelected && 'bg-muted/40',
                                        )}
                                        onClick={() => (selectionMode ? onToggleSelect?.(company.id) : handleRowClick(company.id))}
                                    >
                                        {selectionMode && (
                                            <TableCell className="w-10" onClick={(event) => event.stopPropagation()}>
                                                <Checkbox
                                                    checked={isSelected}
                                                    onCheckedChange={() => onToggleSelect?.(company.id)}
                                                    aria-label={t('companies.tables.select_row', 'Select row')}
                                                />
                                            </TableCell>
                                        )}
                                        {columns.map((column) => {
                                            if (column.id === 'name') {
                                                return (
                                                    <TableCell key={column.id}>
                                                        <Link
                                                            href={`/company/${company.id}`}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="hover:bg-accent text-foreground flex w-full items-center gap-2 rounded px-2 py-1.5 text-left font-semibold transition-colors"
                                                        >
                                                            <span>{company.name}</span>
                                                        </Link>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'type') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <InlineTypeCell
                                                            value={company.type || null}
                                                            onUpdate={
                                                                !readOnly && onUpdate?.type
                                                                    ? async (value) => {
                                                                          await onUpdate.type!(company.id, value);
                                                                      }
                                                                    : undefined
                                                            }
                                                            types={types}
                                                            readOnly={readOnly}
                                                        />
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'company_code') {
                                                return (
                                                    <TableCell key={column.id}>
                                                        <span className="text-muted-foreground font-mono text-sm">{company.company_code || '-'}</span>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'priority') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <InlinePriorityCell
                                                            value={company.priority || null}
                                                            onUpdate={
                                                                !readOnly && onUpdate?.priority
                                                                    ? async (value) => {
                                                                          await onUpdate.priority!(company.id, value);
                                                                      }
                                                                    : undefined
                                                            }
                                                            priorities={priorities}
                                                            readOnly={readOnly}
                                                        />
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'domain') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <InlineDomainCell
                                                            value={company.domain || null}
                                                            onUpdate={
                                                                !readOnly && onUpdate?.domain
                                                                    ? async (value) => {
                                                                          await onUpdate.domain!(company.id, value);
                                                                      }
                                                                    : undefined
                                                            }
                                                            readOnly={readOnly}
                                                        />
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'category') {
                                                // Legacy rows can hold a non-array value in this json column.
                                                const categories = Array.isArray(company.category) ? company.category : [];
                                                const shown = categories.slice(0, MAX_CATEGORY_BADGES);
                                                const overflow = categories.length - shown.length;

                                                return (
                                                    <TableCell key={column.id}>
                                                        {categories.length === 0 ? (
                                                            <span className="text-muted-foreground">—</span>
                                                        ) : (
                                                            <div className="flex flex-wrap items-center gap-1">
                                                                {shown.map((category) => (
                                                                    <Badge key={category} variant="secondary">
                                                                        {settingLabel(t, 'company_categories', category)}
                                                                    </Badge>
                                                                ))}
                                                                {overflow > 0 ? (
                                                                    <Badge variant="outline" title={categories.join(', ')}>
                                                                        +{overflow}
                                                                    </Badge>
                                                                ) : null}
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'country') {
                                                return (
                                                    <TableCell key={column.id}>
                                                        <span className="text-muted-foreground">{company.country || '—'}</span>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'revenue') {
                                                return (
                                                    <TableCell key={column.id}>
                                                        <span className="text-muted-foreground">
                                                            {company.revenue
                                                                ? new Intl.NumberFormat('en-US', {
                                                                      style: 'currency',
                                                                      currency: 'EUR',
                                                                      maximumFractionDigits: 0,
                                                                  }).format(Number(company.revenue))
                                                                : '—'}
                                                        </span>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'employees') {
                                                return (
                                                    <TableCell key={column.id}>
                                                        <span className="text-muted-foreground">{company.employees ?? '—'}</span>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'created_at') {
                                                return (
                                                    <TableCell key={column.id}>
                                                        <span className="text-muted-foreground text-sm">
                                                            {company.created_at ? new Date(company.created_at).toLocaleDateString() : '—'}
                                                        </span>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'total_profit') {
                                                const profit = company.total_profit || 0;
                                                const profitColor = profit >= 0 ? 'text-success-subtle-foreground' : 'text-destructive';
                                                return (
                                                    <TableCell key={column.id}>
                                                        <div className={`text-left font-medium ${profitColor}`}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'EUR',
                                                            }).format(profit)}
                                                        </div>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'total_people') {
                                                const totalPeople = company.total_people || 0;
                                                return (
                                                    <TableCell key={column.id}>
                                                        <div className="flex items-center justify-start">
                                                            <span>{totalPeople}</span>
                                                        </div>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'users') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <InlineAssignedUserCell
                                                            assignedUsers={assignedUsers as unknown as User[]}
                                                            allUsers={users}
                                                            onUpdate={
                                                                !readOnly && onUpdate?.assignedUsers
                                                                    ? async (userIds) => {
                                                                          await onUpdate.assignedUsers!(company.id, userIds);
                                                                      }
                                                                    : undefined
                                                            }
                                                            readOnly={readOnly}
                                                        />
                                                    </TableCell>
                                                );
                                            }

                                            return null;
                                        })}
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
